import {skipToken} from '@reduxjs/toolkit/query';
import {Card} from 'platform/components';
import {Align, Box, Heading, HStack, Show, Space, Text, VStack} from 'platform/foundation';
import {useFormatCurrency, useFormatNumber} from 'platform/locale';

import {isNotNil} from 'ramda';
import {floor, isNumber} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../../i18n/i18n';
import {useGetPriceReportCarDetailQuery} from '../../../../store/priceReportApi';
import {MarketType} from '../../../../types/MarketType';
import {PriceReportType} from '../../../../types/PriceReportType';
import {useIsPrintForced} from '../../hooks/useIsPrintForced';
import {AccuracyChart} from './components/AccuracyChart';
import {AdvertisedPriceChart} from './components/AdvertisedPriceChart';
import {CarvagoPriceChart} from './components/CarvagoPriceChart';
import {RealSellingPriceBanners} from './components/RealSellingPriceBanners';

interface RealSellingPriceCardProps extends TestIdProps {
  priceReport: PriceReportType;
  market?: MarketType;
}

export function RealSellingPriceCard(props: RealSellingPriceCardProps) {
  const {isPrint} = useIsPrintForced();
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();

  const {data: advertisedVehicle} = useGetPriceReportCarDetailQuery(
    isNotNil(props.priceReport?.vehicle.adId)
      ? {
          reportId: props.priceReport.id,
          vehicleId: props.priceReport.vehicle.adId,
        }
      : skipToken
  );

  return (
    <Card variant={isPrint ? 'inlineWhite' : undefined} data-testid={props['data-testid']}>
      <Box padding={2} overflow="hidden">
        <Align center>
          <Heading size={2} data-testid={suffixTestId('realSellingPrice', props)}>
            {i18n.t('entity.priceMeter.labels.realSellingPrice')}
          </Heading>
        </Align>
        <Space vertical={6} />
        <VStack align="center">
          <CarvagoPriceChart
            minPrice={props.priceReport.sellingPrice.min}
            maxPrice={props.priceReport.sellingPrice.max}
            recommendedPrice={props.priceReport.sellingPrice.recommended}
            data-testid={suffixTestId('priceChart', props)}
          />
          <Show when={isNumber(props.priceReport.accuracy) || isNotNil(advertisedVehicle)}>
            <Box width="100%" paddingTop={10} paddingBottom={4}>
              <HStack spacing={4} justify="space-around" width="100%" wrap>
                <AdvertisedPriceChart
                  priceReport={props.priceReport}
                  data-testid={suffixTestId('advertisedPriceChart', props)}
                />
                <AccuracyChart
                  priceReport={props.priceReport}
                  data-testid={suffixTestId('accuracyChart', props)}
                />
              </HStack>
            </Box>
          </Show>
        </VStack>
        <Space vertical={6} />
        <Align center>
          <Heading size={3} data-testid={suffixTestId('summaryPricing', props)}>
            {i18n.t('entity.priceMeter.labels.summaryPricing')}
          </Heading>
        </Align>
        <Space vertical={6} />
        <Text
          color="secondary"
          size="small"
          data-testid={suffixTestId('summaryPricingDescription', props)}
        >
          {i18n.t('entity.priceMeter.labels.summaryPricingDescription')}
        </Text>
        <Space vertical={4} />
        <Text
          color="secondary"
          size="small"
          data-testid={suffixTestId('summaryPricingYourVehicle', props)}
        >
          {i18n.t(
            `entity.priceMeter.labels.summaryPricingYourVehicle.${
              (props.market?.sold?.similarVehicle ?? 0) >= 10
                ? 'knownSimilarCount'
                : 'unknownSimilarCount'
            }`,
            {
              price: formatCurrency(
                props.priceReport.sellingPrice.recommended.value,
                props.priceReport.sellingPrice.recommended.currency
              ),
              similarVehicles: formatNumber(props.market?.sold?.similarVehicle),
            }
          )}
          {isNotNil(props.priceReport.turnover) &&
            ` ${i18n.t('entity.priceMeter.labels.summarySellingTimeYourVehicle', {
              days: formatNumber(floor(props.priceReport.turnover)),
            })}`}
        </Text>
        <Space vertical={6} />
        <RealSellingPriceBanners
          priceReport={props.priceReport}
          market={props.market}
          data-testid={suffixTestId('banners', props)}
        />
      </Box>
    </Card>
  );
}
